/**
 * @fileoverview Pure helper that computes the per-template violation
 * inventory consumed by CanvasViolationsPanel.
 * @module violations
 *
 * Responsibilities:
 * - Walk every form/field attached to the template.
 * - Emit one ViolationInventoryEntry per field that is Pass/Fail-enabled
 *   or already has a violationMapping.
 * - Resolve linked code title from the active violation index.
 * - Compute linked/unlinked/stale status.
 *
 * Dependencies:
 * - ../types/templateTypes
 * - ../types/violationCodeSet
 *
 * Architecture:
 * - Pure function. No Dexie/network I/O. Host apps feed already-loaded
 *   violation index entries for the active default code set.
 *
 * Notes:
 * - The plan does NOT change the underlying storage shape: the canonical
 *   shape is `field.violationMappings[0]` (ViolationMapping). Legacy
 *   `field.extras.failViolation` shapes are read as a fallback so pre-plan
 *   data doesn't disappear from the inventory.
 */

import type { TemplateBuilderTemplate, TemplateBuilderForm, FieldType, LayoutSection, ViolationMapping } from '../types/templateTypes';
import type { ViolationIndexEntry } from '../types/violationCodeSet';

/**
 * @contract ViolationInventoryEntry
 *
 * Purpose:
 * - One row in the Violations panel card list.
 *
 * Fields:
 * - formId / formName / formOrder: Source form breadcrumb.
 * - sectionId / sectionTitle: Section containing the field (if any).
 * - fieldId / fieldLabel / fieldType: Field identity.
 * - hasPassFail: Whether the field has Pass/Fail scoring enabled.
 * - linkedViolationCode / linkedViolationTitle / linkedViolationText:
 *   Human-readable current binding details (if any).
 * - status: 'linked' | 'unlinked' | 'stale'.
 *
 * Rules:
 * - `status` is computed by {@link buildViolationInventory}.
 *
 * Sync:
 * - Client-only; no persistence of this entry itself.
 */
export interface ViolationInventoryEntry {
  formId: string;
  formName: string;
  formOrder: number;
  sectionId?: string;
  sectionTitle?: string;
  fieldId: string;
  fieldLabel: string;
  fieldType: FieldType;
  hasPassFail: boolean;
  linkedViolationCode?: string;
  linkedViolationTitle?: string;
  linkedViolationText?: string;
  linkedViolationReference?: string;
  linkedViolationSetId?: string;
  status: 'linked' | 'unlinked' | 'stale';
}

export interface BuildViolationInventoryOptions {
  /** Id of the active/default code set. When set, mappings whose codeSetId
   *  differs are candidate-stale and resolved against activeCodeEntries. */
  activeCodeSetId?: string;
  /** Violation index entries for the active code set (used for title
   *  resolution + staleness checks). */
  activeCodeEntries?: ViolationIndexEntry[];
}

interface FieldLocationMeta {
  section?: LayoutSection;
}

function findSectionForField(form: TemplateBuilderForm, fieldId: string): FieldLocationMeta {
  const sections = form.layout?.sections ?? [];
  for (const section of sections) {
    if (section.fieldIds?.includes(fieldId)) return { section };
  }
  return {};
}

function readLegacyFailViolation(extras: Record<string, any> | undefined): { code: string; title?: string; setId?: string } | null {
  if (!extras) return null;
  const single = extras.failViolation;
  if (single && typeof single === 'object' && typeof single.code === 'string') {
    return { code: single.code, title: single.title, setId: single.setId };
  }
  const many = extras.failViolations;
  if (Array.isArray(many) && many.length > 0 && typeof many[0]?.code === 'string') {
    return { code: many[0].code, title: many[0].title, setId: many[0].setId };
  }
  return null;
}

function readCanonicalMapping(mappings: ViolationMapping[] | undefined): {
  code: string;
  setId?: string;
  title?: string;
  text?: string;
  reference?: string;
} | null {
  if (!Array.isArray(mappings) || mappings.length === 0) return null;
  const first = mappings[0];
  if (!first || typeof first.code !== 'string' || first.code === '') return null;
  return {
    code: first.code,
    setId: first.codeSetId || undefined,
    title: first.title,
    text: first.contentSnippet,
    reference: first.codeReference,
  };
}

/**
 * @process buildViolationInventory
 *
 * Steps:
 * 1. Iterate `template.formRefs` in order.
 * 2. For each referenced form, iterate fields.
 * 3. Include the field if `extras.hasPassFail === true` OR a violation
 *    binding is present (canonical `violationMappings[0]` or legacy
 *    `extras.failViolation(s)`).
 * 4. Resolve title via `activeCodeEntries` when possible.
 * 5. Compute status: unlinked (no binding) / stale (binding doesn't match
 *    active set or its code is absent) / linked (otherwise).
 *
 * Inputs:
 * - template, forms map, optional active code set metadata.
 *
 * Outputs:
 * - Ordered list of ViolationInventoryEntry.
 *
 * Failure Handling:
 * - Returns `[]` on empty/missing template data.
 *
 * Idempotency:
 * - REQUIRED (pure).
 */
export function buildViolationInventory(
  template: TemplateBuilderTemplate,
  forms: Map<string, TemplateBuilderForm>,
  options: BuildViolationInventoryOptions = {},
): ViolationInventoryEntry[] {
  const entries: ViolationInventoryEntry[] = [];
  const { activeCodeSetId, activeCodeEntries } = options;
  const activeCodeMap = new Map<string, ViolationIndexEntry>();
  if (activeCodeEntries) {
    for (const e of activeCodeEntries) activeCodeMap.set(e.code, e);
  }

  const refs = [...(template.formRefs ?? [])].sort((a, b) => a.order - b.order);
  for (const ref of refs) {
    const form = forms.get(ref.formId);
    if (!form) continue;
    for (const field of form.fields ?? []) {
      const extras = field.extras || {};
      const hasPassFail = !!extras.hasPassFail;
      const canonical = readCanonicalMapping(field.violationMappings);
      const legacy = canonical ? null : readLegacyFailViolation(extras);
      const binding = canonical ?? legacy;

      if (!hasPassFail && !binding) continue;

      const loc = findSectionForField(form, field.id);
      let status: ViolationInventoryEntry['status'] = 'unlinked';
      let linkedTitle: string | undefined;
      let linkedText: string | undefined;
      let linkedReference: string | undefined;

      if (binding) {
        const activeEntry = activeCodeMap.get(binding.code);
        const codeExistsInActive = activeCodeEntries ? !!activeEntry : false;
        const setMatches = activeCodeSetId ? binding.setId === activeCodeSetId : true;

        if (activeCodeSetId && (!setMatches || !codeExistsInActive)) {
          status = 'stale';
        } else {
          status = 'linked';
        }
        linkedTitle = (binding as any).title ?? activeEntry?.title;
        linkedText = (binding as any).text ?? activeEntry?.contentSnippet;
        linkedReference = (binding as any).reference ?? activeEntry?.codeReference;
      }

      entries.push({
        formId: ref.formId,
        formName: form.name,
        formOrder: ref.order,
        sectionId: loc.section?.id,
        sectionTitle: loc.section?.title,
        fieldId: field.id,
        fieldLabel: field.label || field.id,
        fieldType: field.type,
        hasPassFail,
        linkedViolationCode: binding?.code,
        linkedViolationTitle: linkedTitle,
        linkedViolationText: linkedText,
        linkedViolationReference: linkedReference,
        linkedViolationSetId: binding?.setId,
        status,
      });
    }
  }

  return entries;
}

/** Suggest a stale entry's replacement from the active code set when the
 *  same `code` exists. Otherwise returns null (the UI surfaces the Fix
 *  action for manual resolution). */
export function suggestStaleReplacement(
  entry: ViolationInventoryEntry,
  activeCodeEntries: ViolationIndexEntry[],
): ViolationIndexEntry | null {
  if (entry.status !== 'stale' || !entry.linkedViolationCode) return null;
  return activeCodeEntries.find((e) => e.code === entry.linkedViolationCode) ?? null;
}
